import classNames from 'classnames';
import React, { FormEvent, Ref, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { ButtonTypes, StandardButton } from '@eika/button';
import { CurrencyInput } from '@eika/currency-input';
import { Input } from '@eika/input';

import { DatoInput, getDateWithCorrectTimezone } from '../../components/dato-input/dato-input';
import { SporsmalHeader } from '../../components/sporsmal-header/sporsmal-header';
import { UtleggHeaderTekst, UtleggType } from '../../types/generated-types';
import { formatNumber } from '../format';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { CurrentUtleggslinje } from '../reducer/current-steg';
import { addUtleggslinje, updateUtleggslinje, updateUtleggslinjeDato } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { CurrentUtleggsporsmal, utleggslinjeContainsInformation } from './utlegg-types';

import './utlegg.scss';

export enum UtleggslinjeFelt {
    Beskrivelse = 'Beskrivelse',
    Dato = 'Dato',
    Utgift = 'Utgift',
    Refundert = 'Refundert',
    DagensPris = 'DagensPris',
}

export interface UpdateUtleggslinjeData {
    id: number;
    updatedData: string;
    felt: UtleggslinjeFelt;
}

const isValidUtleggslinjeFelt = (type: UtleggslinjeFelt, utleggslinje: CurrentUtleggslinje): boolean => {
    switch (type) {
        case UtleggslinjeFelt.Beskrivelse:
            return utleggslinje.beskrivelse.length > 0;
        case UtleggslinjeFelt.Dato:
            return !!utleggslinje.dato;
        case UtleggslinjeFelt.Utgift:
            return Number(utleggslinje.utgift) > 0;
        case UtleggslinjeFelt.Refundert:
            return Number(utleggslinje.refundert) >= 0;
        default:
            return true;
    }
};

const utleggslinjeHasNonNegativeSum = (utleggslinje: CurrentUtleggslinje) =>
    Number(utleggslinje.utgift) >= Number(utleggslinje.refundert);

const isValidUtleggslinjeWithDato = (utleggslinje: CurrentUtleggslinje) =>
    isValidUtleggslinjeFelt(UtleggslinjeFelt.Beskrivelse, utleggslinje) &&
    isValidUtleggslinjeFelt(UtleggslinjeFelt.Dato, utleggslinje) &&
    isValidUtleggslinjeFelt(UtleggslinjeFelt.Utgift, utleggslinje) &&
    isValidUtleggslinjeFelt(UtleggslinjeFelt.Refundert, utleggslinje) &&
    utleggslinjeHasNonNegativeSum(utleggslinje);

const isValidUtleggslinjeWithoutDato = (utleggslinje: CurrentUtleggslinje) =>
    isValidUtleggslinjeFelt(UtleggslinjeFelt.Beskrivelse, utleggslinje) &&
    isValidUtleggslinjeFelt(UtleggslinjeFelt.Utgift, utleggslinje) &&
    isValidUtleggslinjeFelt(UtleggslinjeFelt.Refundert, utleggslinje) &&
    utleggslinjeHasNonNegativeSum(utleggslinje);

const isValidUtleggslinje = (utleggslinje: CurrentUtleggslinje, containsDato: boolean): boolean => {
    if (!utleggslinjeContainsInformation(utleggslinje)) {
        return true;
    }
    if (containsDato) {
        return isValidUtleggslinjeWithDato(utleggslinje);
    }
    return isValidUtleggslinjeWithoutDato(utleggslinje);
};

export const isValidUtleggslinjer = (utleggslinjer: CurrentUtleggslinje[], containsDato: boolean) =>
    utleggslinjer.every((utleggslinje) => isValidUtleggslinje(utleggslinje, containsDato));

const getFeilmeldingForUleggslinjeFelt = (utleggslinjeFelt: UtleggslinjeFelt) => {
    switch (utleggslinjeFelt) {
        case UtleggslinjeFelt.Beskrivelse:
            return 'Utlegget må ha en beskrivelse';
        case UtleggslinjeFelt.Dato:
            return 'Legg inn dato';
        case UtleggslinjeFelt.Utgift:
            return 'Sett beløp';
        case UtleggslinjeFelt.Refundert:
            return 'Sett beløp';
        default:
            return '';
    }
};

const showFeilmeldingForFelt = (
    showFeilmeldinger: boolean,
    utleggslinje: CurrentUtleggslinje,
    felt: UtleggslinjeFelt,
) => showFeilmeldinger && utleggslinjeContainsInformation(utleggslinje) && !isValidUtleggslinjeFelt(felt, utleggslinje);

const getFeilmelding = (showFeilmeldinger: boolean, utleggslinje: CurrentUtleggslinje, felt: UtleggslinjeFelt) =>
    showFeilmeldingForFelt(showFeilmeldinger, utleggslinje, felt) && getFeilmeldingForUleggslinjeFelt(felt);

const showInputFeltError = (showFeilmeldinger: boolean, utleggslinje: CurrentUtleggslinje, felt: UtleggslinjeFelt) => {
    switch (felt) {
        case UtleggslinjeFelt.Beskrivelse:
            return showFeilmeldingForFelt(showFeilmeldinger, utleggslinje, felt);
        case UtleggslinjeFelt.Utgift:
            return (
                showFeilmeldingForFelt(showFeilmeldinger, utleggslinje, felt) ||
                !utleggslinjeHasNonNegativeSum(utleggslinje)
            );
        case UtleggslinjeFelt.Refundert:
            return (
                showFeilmeldingForFelt(showFeilmeldinger, utleggslinje, felt) ||
                !utleggslinjeHasNonNegativeSum(utleggslinje)
            );
        default:
            return false;
    }
};

const firstFieldInUtleggslinjeWithError = (
    utleggslinje: CurrentUtleggslinje,
    containsDato: boolean,
): UtleggslinjeFelt => {
    const fieldOrder = [
        UtleggslinjeFelt.Beskrivelse,
        containsDato ? UtleggslinjeFelt.Dato : undefined,
        UtleggslinjeFelt.Utgift,
    ].filter((field) => field !== undefined);
    return fieldOrder.find((field) => !isValidUtleggslinjeFelt(field, utleggslinje)) || UtleggslinjeFelt.Beskrivelse;
};

const findFirstFieldWithError = (
    currentSporsmal: CurrentUtleggsporsmal,
): { utleggslinjeId: number; felt: UtleggslinjeFelt } | undefined => {
    const firstInvalidUtleggslinje = currentSporsmal.utleggslinjer.find(
        (utleggslinje) => !isValidUtleggslinje(utleggslinje, currentSporsmal.sporsmal.toggles.includeDato),
    );
    if (!firstInvalidUtleggslinje) {
        return undefined;
    }
    return {
        utleggslinjeId: firstInvalidUtleggslinje.id,
        felt: firstFieldInUtleggslinjeWithError(firstInvalidUtleggslinje, currentSporsmal.sporsmal.toggles.includeDato),
    };
};

const errorRefOnlyIfFieldIsFirstWithError = (
    utleggslinje: CurrentUtleggslinje,
    field: UtleggslinjeFelt,
    errorRef?: Ref<FocusableHtmlElement>,
    firstFieldWithError?: { utleggslinjeId: number; felt: UtleggslinjeFelt },
): Ref<FocusableHtmlElement> | undefined => {
    if (utleggslinje.id === firstFieldWithError?.utleggslinjeId && field === firstFieldWithError?.felt) {
        return errorRef;
    }
    return undefined;
};

interface UtleggslinjeProps {
    utleggslinje: CurrentUtleggslinje;
    onUtleggslinjeChange: (updateUtleggslinjeData: UpdateUtleggslinjeData) => void;
    onDatoChange: (utleggslinjeId: number, dato: ZonedDate) => void;
    showFeilmeldinger: boolean;
    focusOnMount: boolean;
    includeRefusjon: boolean;
    includeDato: boolean;
    headerTekst: UtleggHeaderTekst;
    firstFieldWithError?: { utleggslinjeId: number; felt: UtleggslinjeFelt };
    errorRef?: Ref<FocusableHtmlElement>;
}

const Utleggslinje = ({
    utleggslinje,
    onUtleggslinjeChange,
    onDatoChange,
    showFeilmeldinger,
    focusOnMount,
    includeRefusjon,
    includeDato,
    headerTekst,
    firstFieldWithError,
    errorRef,
}: UtleggslinjeProps) => {
    const beskrivelseInputRef = useFocusRef<HTMLInputElement>(focusOnMount);

    return (
        <>
            <div
                className={classNames({
                    'input-error': showInputFeltError(showFeilmeldinger, utleggslinje, UtleggslinjeFelt.Beskrivelse),
                })}
            >
                <label
                    className="utlegg-input-label__mobile-view sgw-input__input-mainlabel"
                    htmlFor={`beskrivelse-rad-${utleggslinje.id}`}
                >
                    {headerTekst.beskrivelse}
                </label>
                <Input
                    id={`beskrivelse-rad-${utleggslinje.id}`}
                    type="text"
                    className="form-control"
                    autoComplete="off"
                    value={utleggslinje.beskrivelse}
                    onChange={(event: FormEvent<HTMLInputElement>) =>
                        onUtleggslinjeChange({
                            id: utleggslinje.id,
                            updatedData: event.currentTarget.value,
                            felt: UtleggslinjeFelt.Beskrivelse,
                        })
                    }
                    ref={mergeRefs([
                        beskrivelseInputRef,
                        errorRefOnlyIfFieldIsFirstWithError(
                            utleggslinje,
                            UtleggslinjeFelt.Beskrivelse,
                            errorRef,
                            firstFieldWithError,
                        ),
                    ])}
                    aria-label={headerTekst.beskrivelse}
                    error={getFeilmelding(showFeilmeldinger, utleggslinje, UtleggslinjeFelt.Beskrivelse)}
                />
            </div>

            {includeDato && (
                <div className="utlegg-dato-celle">
                    <label className="utlegg-input-label__mobile-view sgw-input__input-mainlabel">
                        {headerTekst.dato}
                    </label>
                    <DatoInput
                        date={utleggslinje.dato ? getZonedDate(new Date(utleggslinje.dato)) : undefined}
                        onChange={(date) => {
                            onDatoChange(utleggslinje.id, date);
                        }}
                        maxDate={getZonedDate(new Date())}
                        placeholder="dd.mm.åååå"
                        error={getFeilmelding(showFeilmeldinger, utleggslinje, UtleggslinjeFelt.Dato)}
                        focusOnMount={false}
                        errorRef={errorRefOnlyIfFieldIsFirstWithError(
                            utleggslinje,
                            UtleggslinjeFelt.Dato,
                            errorRef,
                            firstFieldWithError,
                        )}
                        id={`dato-sporsmal-${utleggslinje.id}`}
                    />
                </div>
            )}
            <div>
                <label
                    className="utlegg-input-label__mobile-view sgw-input__input-mainlabel"
                    htmlFor={`utlegg-rad-${utleggslinje.id}`}
                >
                    {headerTekst.utlegg}
                </label>

                <CurrencyInput
                    name={`utlegg-rad-${utleggslinje.id}`}
                    id={`utlegg-rad-${utleggslinje.id}`}
                    className="form-control utlegg-number"
                    autoComplete="off"
                    value={utleggslinje.utgift}
                    onChange={(numberFormatValues: {
                        type: string;
                        target: { name: string; value: string | number };
                    }) => {
                        onUtleggslinjeChange({
                            id: utleggslinje.id,
                            updatedData: numberFormatValues.target.value.toString(),
                            felt: UtleggslinjeFelt.Utgift,
                        });
                    }}
                    aria-label={headerTekst.utlegg}
                    error={getFeilmelding(showFeilmeldinger, utleggslinje, UtleggslinjeFelt.Utgift)}
                    ref={
                        errorRefOnlyIfFieldIsFirstWithError(
                            utleggslinje,
                            UtleggslinjeFelt.Utgift,
                            errorRef,
                            firstFieldWithError,
                        ) as Ref<HTMLInputElement>
                    }
                />
            </div>
            {includeRefusjon && (
                <div className="last-table-cell">
                    <label
                        className="utlegg-input-label__mobile-view sgw-input__input-mainlabel"
                        htmlFor={`refusjon-rad-${utleggslinje.id}`}
                    >
                        {headerTekst.refusjon}
                    </label>

                    {utleggslinje.utleggType === UtleggType.WithRefusjon && (
                        <CurrencyInput
                            name={`refusjon-rad-${utleggslinje.id}`}
                            id={`refusjon-rad-${utleggslinje.id}`}
                            className="form-control utlegg-number"
                            autoComplete="off"
                            value={utleggslinje.refundert}
                            onChange={(numberFormatValues: {
                                type: string;
                                target: { name: string; value: string | number };
                            }) =>
                                onUtleggslinjeChange({
                                    id: utleggslinje.id,
                                    updatedData: numberFormatValues.target.value.toString(),
                                    felt: UtleggslinjeFelt.Refundert,
                                })
                            }
                            aria-label={headerTekst.refusjon}
                            error={getFeilmelding(showFeilmeldinger, utleggslinje, UtleggslinjeFelt.Refundert)}
                            ref={
                                errorRefOnlyIfFieldIsFirstWithError(
                                    utleggslinje,
                                    UtleggslinjeFelt.Refundert,
                                    errorRef,
                                    firstFieldWithError,
                                ) as Ref<HTMLInputElement>
                            }
                        />
                    )}

                    {utleggslinje.utleggType === UtleggType.WithDagensPris && (
                        <CurrencyInput
                            name={`refusjon-rad-${utleggslinje.id}`}
                            id={`refusjon-rad-${utleggslinje.id}`}
                            className="form-control utlegg-number"
                            autoComplete="off"
                            value={utleggslinje.dagensPris}
                            onChange={(numberFormatValues: {
                                type: string;
                                target: { name: string; value: string | number };
                            }) =>
                                onUtleggslinjeChange({
                                    id: utleggslinje.id,
                                    updatedData: numberFormatValues.target.value.toString(),
                                    felt: UtleggslinjeFelt.DagensPris,
                                })
                            }
                            aria-label={headerTekst.refusjon}
                            ref={
                                errorRefOnlyIfFieldIsFirstWithError(
                                    utleggslinje,
                                    UtleggslinjeFelt.DagensPris,
                                    errorRef,
                                    firstFieldWithError,
                                ) as Ref<HTMLInputElement>
                            }
                        />
                    )}
                </div>
            )}
        </>
    );
};

const parseUtleggslinjeNumber = (value: string) => {
    return Number(value);
};

const sumNumbers = (values: string[]) => values.map(parseUtleggslinjeNumber).reduce((acc, element) => acc + element, 0);

const nettoUtlegg = (utleggslinjer: CurrentUtleggslinje[]) => {
    if (utleggslinjer.length === 0) {
        return 0;
    }
    const { utleggType } = utleggslinjer[0];
    switch (utleggType) {
        case UtleggType.WithDagensPris:
            return sumNumbers(utleggslinjer.map((linje) => linje.dagensPris));
        case UtleggType.WithRefusjon:
        default: {
            const sumUtgifter = sumNumbers(utleggslinjer.map((linje) => linje.utgift));
            const sumRefusjoner = sumNumbers(utleggslinjer.map((linje) => linje.refundert));
            return sumUtgifter - sumRefusjoner;
        }
    }
};

interface Props {
    currentSporsmal: CurrentUtleggsporsmal;
    showFeilmeldinger: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const Utlegg = ({ currentSporsmal, showFeilmeldinger, errorRef, focusOnMount = true }: Props) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (utleggslinje: UpdateUtleggslinjeData) => {
        dispatch(updateUtleggslinje(utleggslinje));
    };

    const onDatoChange = (utleggslinjeId: number, date: ZonedDate) => {
        if (date !== undefined) {
            const dateWithCorrectTimezone = getDateWithCorrectTimezone(date);
            dispatch(updateUtleggslinjeDato(utleggslinjeId, dateWithCorrectTimezone));
        }
    };

    useEffect(() => {
        if (currentSporsmal.utleggslinjer.length === 0) {
            dispatch(addUtleggslinje());
        }
    }, [dispatch, currentSporsmal.utleggslinjer.length]);

    const onAddUtleggClick = () => {
        dispatch(addUtleggslinje());
    };

    const firstFieldWithError = findFirstFieldWithError(currentSporsmal);

    const { headerTekst, toggles } = currentSporsmal.sporsmal;

    return (
        <SporsmalHeader tittel={currentSporsmal.sporsmal.tittel} ingress={currentSporsmal.sporsmal.ingress} extraWide>
            <div
                className={classNames('utlegg-tabell', {
                    'utlegg-tabell-uten-refusjon': !toggles.includeRefusjon || !toggles.includeDato,
                    'utlegg-tabell-uten-refusjon-og-dato': !toggles.includeRefusjon && !toggles.includeDato,
                })}
            >
                <div className="utlegg-tabell-header">{headerTekst.beskrivelse}</div>
                {toggles.includeDato && <div className="utlegg-tabell-header">{headerTekst.dato}</div>}
                <div className="utlegg-tabell-header">{headerTekst.utlegg}</div>
                {toggles.includeRefusjon && <div className="utlegg-tabell-header">{headerTekst.refusjon}</div>}

                {currentSporsmal.utleggslinjer.map((utleggslinje, index) => (
                    <Utleggslinje
                        utleggslinje={utleggslinje}
                        onUtleggslinjeChange={onChange}
                        onDatoChange={onDatoChange}
                        key={utleggslinje.id}
                        showFeilmeldinger={showFeilmeldinger}
                        focusOnMount={focusOnMount && index === 0}
                        includeRefusjon={toggles.includeRefusjon}
                        includeDato={toggles.includeDato}
                        headerTekst={headerTekst}
                        firstFieldWithError={firstFieldWithError}
                        errorRef={errorRef}
                    />
                ))}
                {toggles.includeAddNewUtleggslinjeButton && (
                    <div className="legg-til-rad-container">
                        <StandardButton
                            id="add-utleggslinje"
                            type="button"
                            onClick={onAddUtleggClick}
                            buttonType={ButtonTypes.STANDARD_NEGATIVE}
                        >
                            <i className="ikon-pluss-24" aria-hidden="true" />
                            Legg til linje
                        </StandardButton>
                    </div>
                )}
            </div>
            <div id="utlegg-oppsummering" className="utlegg-oppsummering">
                Du søker dekning for {formatNumber(nettoUtlegg(currentSporsmal.utleggslinjer))} kr
            </div>
            {currentSporsmal.utleggslinjer.some((linje) => !utleggslinjeHasNonNegativeSum(linje)) && (
                <p role="alert" className="error-message-utlegg" id="utlegg-oppsummering-error">
                    Refundert beløp kan ikke være høyere enn pris.
                </p>
            )}
        </SporsmalHeader>
    );
};
